import React from 'react'
import { withTheme } from '@emotion/react'

import {
    StyledByline,
    StyledBylineAuthor,
    StyledSponsoredLabel,
    StyledHeadshot,
    StyledHeadshotWrapper,
    StyledBylineText,
    StyledProfileEmailButton,
    StyledTimestamp,
    StyledBylineTextBlock,
    StyledBylineTextWrap,
    StyledMiddleDot,
    StyledSourceWrapper,
    StyledSourceLogo,
    StyledPremiumIcon,
    StyledPremiumWrapper,
    StyledBylineTextWrapper,
    StyledMetaDataWrapper,
    StyledLabelWrapper,
    StyledBylinePosition,
} from 'component-library/publication/Byline/Byline.styled'
import { getSourceLogo } from '../SourceLogo/SourceLogo'

import { Theme } from 'component-library/__styling/themes'
import { createRenderTarget } from 'component-library/render-target'
import { calcRem } from 'component-library/__styling/style-functions'
import { AmpImage } from 'component-library/content/Picture/Picture.amp'
import {
    getImageSizePropsFromType,
    ImageSizeTypes,
} from 'component-library/content/Picture/layouts/helpers/sizes'
import { Product } from 'component-library/__product'
import { LiveLabel } from 'component-library/typography/LiveLabel/LiveLabel'
import { Profile } from 'common/client-data-types/Profile'
import { FeatureToggle } from 'component-library/feature-toggling/Toggle/Toggle'

export interface Props {
    text?: string
    profile?: Profile[]
    source?: string
    isSponsored?: boolean
    isLive?: boolean
    timestamp?: string // make reqire
    url?: string
    title?: string
    isFullWidth: boolean
    isPremium?: boolean
}

const getImageWidth = (product: Product) => {
    switch (product) {
        case Product.PerthNow:
            return 106
        case Product.TheWest:
            return 110 // This card not used on the west atm
        case Product.SevenNews:
            return 126
    }
}

const getImage = (profile: Profile[]) => {
    const profileImage = profile.slice(0).map(item => {
        return item.thumbnailPhoto ? item.thumbnailPhoto : undefined
    })

    return `${profileImage}`
}

export interface SrcLogoProps {
    source: string
    logoSrc: {
        src: string
        width: number
        height: number
        maxWidth: number
    }
}

const SrcLogoWeb: React.FC<SrcLogoProps> = ({ source, logoSrc }) => (
    <StyledSourceLogo alt={source} src={logoSrc.src} maxWidth={logoSrc.maxWidth} />
)

const SrcLogo = createRenderTarget<SrcLogoProps>('SrcLogo', {
    web: ({ source, logoSrc }) => <SrcLogoWeb source={source} logoSrc={logoSrc} />,
    amp: ({ source, logoSrc }) => (
        <AmpImage
            width={logoSrc.width}
            height={logoSrc.height}
            alt={source}
            src={logoSrc.src}
            sizes={calcRem(logoSrc.maxWidth)}
        />
    ),
    rss: ({ source, logoSrc }) => <img src={logoSrc.src} alt={source} />,
    preview: SrcLogoWeb,
})

class InternalByline extends React.Component<Props & { theme: Theme }> {
    getSource(source: string) {
        const { theme } = this.props
        const isSevenTheme = theme.kind === 'sevennews'
        const defaultSource = (
            <StyledBylineTextBlock isSource isLive={this.props.isLive}>
                {source}
            </StyledBylineTextBlock>
        )
        const logoSrc = source ? getSourceLogo(source) : null

        return isSevenTheme ? (
            <React.Fragment>
                <StyledMiddleDot />
                {source && (
                    <StyledSourceWrapper>
                        {logoSrc ? <SrcLogo source={source} logoSrc={logoSrc} /> : source}
                    </StyledSourceWrapper>
                )}
            </React.Fragment>
        ) : (
            defaultSource
        )
    }

    getText(inlineTimestamp: boolean) {
        const timestamp = inlineTimestamp && this.renderTimestamp()

        const { profile, text, source, isSponsored, theme } = this.props

        if (profile && profile.length !== 0 && !isSponsored) {
            const profileBlock = profile.map((item, index) => {
                const profileUrl = `/profile/${item.slug}`

                if (source) {
                    return (
                        <StyledBylineTextWrap key={index}>
                            <StyledBylineAuthor
                                href={profileUrl}
                                itemScope
                                itemType="http://schema.org/Person"
                            >
                                <span itemProp="author name">{item.name}</span>
                            </StyledBylineAuthor>
                            {this.getSource(source)}
                            {timestamp}
                        </StyledBylineTextWrap>
                    )
                } else if (item.position) {
                    return (
                        <StyledBylineTextWrap key={index}>
                            <StyledBylineAuthor
                                href={profileUrl}
                                itemScope
                                itemType="http://schema.org/Person"
                            >
                                <span itemProp="author name">{item.name}</span>
                            </StyledBylineAuthor>
                            <StyledBylinePosition itemProp="jobTitle">
                                {item.position}
                            </StyledBylinePosition>
                        </StyledBylineTextWrap>
                    )
                } else {
                    return (
                        <StyledBylineAuthor
                            key={index}
                            href={profileUrl}
                            itemScope
                            itemType="http://schema.org/Person"
                        >
                            <span itemProp="author name">{item.name}</span>
                        </StyledBylineAuthor>
                    )
                }
            })

            return profileBlock
        }

        // Sevennews doenst have designs for the sponsored label yet
        if (isSponsored && !(theme.kind === 'sevennews')) {
            if (profile && profile.length !== 0) {
                const profileBlock = profile.map((item, index) => {
                    const profileUrl = `/profile/${item.slug}`

                    return (
                        <StyledBylineTextWrap key={index}>
                            {item.name && (
                                <StyledBylineAuthor
                                    href={profileUrl}
                                    itemScope
                                    itemType="http://schema.org/Person"
                                >
                                    <span itemProp="author name">{item.name}</span>
                                </StyledBylineAuthor>
                            )}
                            <StyledSponsoredLabel />
                        </StyledBylineTextWrap>
                    )
                })

                return profileBlock
            }
            return (
                <StyledBylineTextWrap>
                    {text && (
                        <StyledBylineTextBlock itemScope itemType="http://schema.org/Person">
                            <span itemProp="author name">{text}</span>
                        </StyledBylineTextBlock>
                    )}
                    <StyledSponsoredLabel />
                </StyledBylineTextWrap>
            )
        }

        if (text && source) {
            return (
                <StyledBylineTextWrap>
                    <StyledBylineTextBlock itemScope itemType="http://schema.org/Person">
                        <span itemProp="author name">{text}</span>
                    </StyledBylineTextBlock>
                    {this.getSource(source)}
                    {timestamp}
                </StyledBylineTextWrap>
            )
        } else if (text) {
            return (
                <StyledBylineTextBlock>
                    <StyledBylineTextBlock itemScope itemType="http://schema.org/Person">
                        <span itemProp="author name">{text}</span>
                    </StyledBylineTextBlock>
                    {timestamp}
                </StyledBylineTextBlock>
            )
        } else if (source) {
            return (
                <StyledBylineTextBlock>
                    {this.getSource(source)}
                    {timestamp}
                </StyledBylineTextBlock>
            )
        } else {
            return ''
        }
    }

    renderTimestamp() {
        const { timestamp, theme } = this.props
        const timestampIconVisible = theme.kind === 'sevennews'

        return (
            timestamp && (
                <StyledTimestamp iconVisible={timestampIconVisible} timestamp={timestamp} />
            )
        )
    }

    renderProfile() {
        const { profile, theme } = this.props
        const showProfile = theme.kind !== 'sevennews'
        const imageWidths = getImageWidth(theme.kind)

        return (
            showProfile &&
            profile &&
            getImage(profile).length !== 0 && (
                <StyledHeadshotWrapper>
                    <StyledHeadshot
                        image={{
                            altText: profile[0] ? `Headshot of ${profile[0].name}` : undefined,
                            crops: {
                                original: {
                                    reference: getImage(profile),
                                    width: 53,
                                    height: 53,
                                    ratio: { width: 1, height: 1 },
                                },
                            },
                        }}
                        imageSizes={getImageSizePropsFromType(
                            theme.kind,
                            ImageSizeTypes.ProfileHeadshot,
                            imageWidths
                        )}
                        imageWidths={imageWidths}
                        layout="object-fit-contain"
                    />
                </StyledHeadshotWrapper>
            )
        )
    }

    renderByline() {
        const { isLive, theme, isPremium } = this.props
        const inlineTimestamp = theme.kind === 'sevennews'
        const bylineText = this.getText(inlineTimestamp)
        const timestamp = !inlineTimestamp && this.renderTimestamp()

        return (
            <StyledBylineText>
                <StyledBylineTextWrapper>
                    {bylineText}{' '}
                    {isLive && (
                        <StyledLabelWrapper>
                            <LiveLabel />
                        </StyledLabelWrapper>
                    )}
                </StyledBylineTextWrapper>
                {(timestamp || isPremium) && (
                    <StyledMetaDataWrapper>
                        {timestamp}
                        {isPremium && (
                            <StyledPremiumWrapper>
                                <StyledPremiumIcon width={17} height={17} />
                            </StyledPremiumWrapper>
                        )}
                    </StyledMetaDataWrapper>
                )}
            </StyledBylineText>
        )
    }

    renderMailTo() {
        const { profile, url, title, theme } = this.props
        const showProfile = theme.kind !== 'sevennews'
        return (
            showProfile &&
            profile &&
            profile.slice(0).map((item, index) => {
                const emailButton = item.email && (
                    <FeatureToggle
                        key={index}
                        feature="email-author"
                        on={() => (
                            <StyledProfileEmailButton
                                text={item.name}
                                email={item.email || ''}
                                url={url}
                                title={title}
                            />
                        )}
                    />
                )
                return emailButton
            })
        )
    }

    render() {
        const { text, source, isSponsored, isFullWidth } = this.props

        if (!text && !source && !isSponsored) {
            return null
        }

        return (
            <StyledByline isFullWidth={isFullWidth}>
                {this.renderProfile()}
                {this.renderByline()}
                {this.renderMailTo()}
            </StyledByline>
        )
    }
}

export const Byline = withTheme(InternalByline)
